import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import {
    Button,
    Card,
    Classes,
    Dialog,
    Icon,
    Tag,
    Tooltip,
} from '@blueprintjs/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpDownLeftRight } from '@fortawesome/pro-solid-svg-icons/faUpDownLeftRight'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faSnowflake } from '@fortawesome/pro-solid-svg-icons/faSnowflake'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup'
import { faRotate } from '@fortawesome/pro-solid-svg-icons/faRotate'
import {
    getSelectedActions,
    bulkUpdateActions,
    toggleFreezeOwner,
    refreshSolution,
    PENDING,
    getAction,
    statusOptions,
} from 'src/store/modules/runbooks/actions'
import {
    openContactSearch,
    toggleControl,
} from 'src/store/modules/ui/runbook/controls'
import { asyncUpdateRunbookSession } from 'src/store/modules/runbooks'
import { pluralize } from 'src/utils/reducer'
import UpdateStatusDialog from '../Dialogs/UpdateStatusDialog'
import ConfirmStatusAlert from '../Dialogs/ConfirmStatusAlert'

import styles from './manageControls.module.scss'

class ManageControls extends Component {
    static propTypes = {
        runbook: PropTypes.object.isRequired,
    }

    state = {
        isOpenUpdateStatus: false,
        showConfirmationModal: false,
        showPropagateModal: false,
        currentActionId: null,
    }

    handleClearSelection = () => {
        const { runbook, selectedActions, actions } = this.props

        actions.bulkUpdateActions(runbook.id, {
            ...selectedActions.reduce(
                (acc, uid) => ({ ...acc, [uid]: { selected: false } }),
                {}
            ),
        })
    }

    handleFullRefresh = () => {
        const { actions } = this.props
        actions.refreshFullRunbook(this.props.runbook.id, {
            avZones: [],
            ascending: true,
        })
    }

    handleChangeOwner = () => {
        const { actions } = this.props

        actions.openContactSearch()
    }

    handleMoveAction = () => {
        const { actions } = this.props

        actions.toggleControl('isMovingAction')
    }

    handleToggleUpdateStatusDialog = () => {
        const { isOpenUpdateStatus } = this.state

        this.setState({ isOpenUpdateStatus: !isOpenUpdateStatus })
    }

    handleUpdatedStatus = (id) => {
        const { runbook, selectedActions, stateFromProps } = this.props
        const actionStatus = statusOptions.byId[id].onSelect
        const actionHasStatusPending =
            selectedActions.length > 0
                ? selectedActions
                      .map((selectedAction) =>
                          getAction(stateFromProps, runbook.id, selectedAction)
                      )
                      .some((action) => action.status === 0)
                : false

        // Store the selected action id
        this.setState({ currentActionId: id })

        if (actionStatus === PENDING || actionHasStatusPending) {
            // Close the update status dialog first
            this.setState({
                isOpenUpdateStatus: false,
                showConfirmationModal: true,
            })
        } else {
            this.checkChildrenAndProceed()
        }
    }

    checkChildrenAndProceed = () => {
        const { runbook, selectedActions, stateFromProps } = this.props

        // Check if any selected action has children
        const hasChildrenActions =
            selectedActions.length > 0
                ? selectedActions
                      .map((selectedAction) =>
                          getAction(stateFromProps, runbook.id, selectedAction)
                      )
                      .some(
                          (action) =>
                              action.children && action.children.length > 0
                      )
                : false

        if (hasChildrenActions) {
            // Close the update status dialog and show the propagate modal
            this.setState({
                isOpenUpdateStatus: false,
                showPropagateModal: true,
            })
        } else {
            // No children, just save with propagate: false
            this.setState(
                {
                    isOpenUpdateStatus: false,
                },
                () => {
                    this.handleSaveUpdatedStatus(false)
                    // Clear selection after successful bulk update
                    this.handleClearSelection()
                }
            )
        }
    }

    handleSaveUpdatedStatus = (propagate = false) => {
        const { runbook, selectedActions, actions } = this.props
        const { currentActionId } = this.state

        const action = statusOptions.byId[currentActionId].onSelect

        // Collect hidden action UIDs from the runbook
        const hiddenActionUids = []
        const actionsById = runbook?.actions?.byId || {}

        for (const uid in actionsById) {
            const act = actionsById[uid]
            if (!act) continue

            // Check if filtered by user or action
            const isFiltered = act.filteredByUser || act.filteredByAction

            // Also check if it's an empty DR Solution (these should be auto-completed)
            const isEmptyDRSolution =
                act.deviceClass === 'DR Solution' &&
                (!act.treeChildren || act.treeChildren.length === 0)

            if (isFiltered || isEmptyDRSolution) {
                hiddenActionUids.push(parseInt(uid))
            }
        }

        actions.asyncUpdateRunbookSession(
            runbook.id,
            selectedActions,
            {
                action,
                propagate,
            },
            hiddenActionUids
        )
    }

    handleFreezeOwner = () => {
        const { runbook, actions, selectedActions } = this.props

        selectedActions.forEach((actionId) => {
            actions.toggleFreezeOwner(runbook.id, actionId)
        })
    }

    handleDeleteAction = () => {
        const { actions } = this.props

        actions.toggleControl('isDeletingAction')
    }

    handleAddNewAction = () => {
        const { actions } = this.props

        actions.toggleControl('isEditingBaseFields')
    }

    toggleConfirmationModalOpen = () => {
        this.setState((prevState) => ({
            showConfirmationModal: !prevState?.showConfirmationModal,
        }))
    }

    togglePropagateModalOpen = () => {
        this.setState((prevState) => ({
            showPropagateModal: !prevState?.showPropagateModal,
        }))
    }

    handlePropagateConfirm = (propagate) => {
        this.setState({ showPropagateModal: false }, () => {
            this.handleSaveUpdatedStatus(propagate)
            // Clear selection after successful bulk update
            this.handleClearSelection()
        })
    }

    handlePropagateCancel = () => {
        this.setState({
            showPropagateModal: false,
            currentActionId: null,
        })
    }

    _renderSelectedAction() {
        const { selectedActions } = this.props

        return (
            <div>
                {selectedActions.length ? (
                    <>
                        <Icon
                            icon="cross"
                            className={styles.crossIcon}
                            iconSize={24}
                            onClick={this.handleClearSelection}
                        />
                        <Tag round className={styles.actionsNrTag}>
                            {selectedActions.length}
                        </Tag>
                        <span className={styles.actionsSelectedText}>
                            {pluralize('action', selectedActions)} selected
                        </span>
                    </>
                ) : null}
            </div>
        )
    }

    _renderActionsButton() {
        const { runbook, selectedActions, awaitingActionUpdate } = this.props

        const hasSelectedActions = selectedActions.length !== 0
        const firstSelectedAction = runbook.actions.byId[selectedActions[0]]
        // Check if the whole selection has the same frozen owner state
        const selectionConsistentlyFrozen =
            hasSelectedActions &&
            !selectedActions.some((selectedAction) => {
                return (
                    runbook.actions.byId[selectedAction].freezeOwnerTeam !==
                    firstSelectedAction.freezeOwnerTeam
                )
            })
        const selectionIsFrozen =
            selectionConsistentlyFrozen && firstSelectedAction.freezeOwnerTeam

        const toggleFreezeButton = (
            <Button
                icon={
                    selectionIsFrozen ? (
                        <Icon icon="flame" />
                    ) : (
                        <FontAwesomeIcon icon={faSnowflake} />
                    )
                }
                intent="geyser"
                disabled={
                    !runbook.isEditing ||
                    !hasSelectedActions ||
                    !selectionConsistentlyFrozen
                }
                large
                className={styles.actionButton}
                onClick={this.handleFreezeOwner}
            >
                {selectionIsFrozen ? 'Unfreeze' : 'Freeze'} owner team
            </Button>
        )

        return (
            <div>
                {runbook.isEditing && (
                    <Button
                        icon={<FontAwesomeIcon icon={faRotate} />}
                        intent="geyser"
                        large
                        className={styles.actionButton}
                        onClick={this.handleFullRefresh}
                    >
                        Refresh runbook
                    </Button>
                )}
                <Button
                    icon={<FontAwesomeIcon icon={faUserGroup} />}
                    intent="geyser"
                    disabled={
                        !(runbook.isEditing || runbook.activeSession) ||
                        !hasSelectedActions
                    }
                    large
                    className={styles.actionButton}
                    onClick={this.handleChangeOwner}
                >
                    Change owner
                </Button>
                {!runbook.activeSession ? (
                    <Button
                        icon={<FontAwesomeIcon icon={faUpDownLeftRight} />}
                        intent="geyser"
                        disabled={!(runbook.isEditing && hasSelectedActions)}
                        large
                        className={styles.actionButton}
                        onClick={this.handleMoveAction}
                    >
                        {pluralize('Move Action', selectedActions)}
                    </Button>
                ) : null}
                {runbook.isEditing ? null : (
                    <Button
                        icon="time"
                        intent="geyser"
                        disabled={
                            !(
                                !runbook.isEditing &&
                                runbook.activeSession &&
                                hasSelectedActions
                            ) || awaitingActionUpdate.length > 0
                        }
                        large
                        className={styles.actionButton}
                        onClick={this.handleToggleUpdateStatusDialog}
                    >
                        Update Status
                    </Button>
                )}

                {!runbook.activeSession ? (
                    selectionConsistentlyFrozen || !hasSelectedActions ? (
                        toggleFreezeButton
                    ) : (
                        <Tooltip
                            content="The current selection contains both freezed and unfreezed actions"
                            position="bottom"
                        >
                            {toggleFreezeButton}
                        </Tooltip>
                    )
                ) : null}
                {!runbook.activeSession ? (
                    <Button
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        intent="danger"
                        disabled={!(runbook.isEditing && hasSelectedActions)}
                        large
                        className={styles.actionButton}
                        onClick={this.handleDeleteAction}
                    >
                        {pluralize('Delete Action', selectedActions)}
                    </Button>
                ) : null}
                {!runbook.activeSession ? (
                    <div className={styles.addActionWrapper}>
                        <Tooltip content="Add action" position="bottom">
                            <Button
                                intent="primary"
                                icon={<FontAwesomeIcon icon={faPlus} />}
                                disabled={!runbook.isEditing}
                                large
                                className={styles.addActionButton}
                                onClick={this.handleAddNewAction}
                            />
                        </Tooltip>
                    </div>
                ) : null}
            </div>
        )
    }

    render() {
        const { isOpenUpdateStatus, showPropagateModal } = this.state

        return (
            <Card className={styles.root}>
                {this._renderSelectedAction()}
                {this._renderActionsButton()}
                <UpdateStatusDialog
                    open={isOpenUpdateStatus}
                    onClose={this.handleToggleUpdateStatusDialog}
                    onSelect={this.handleUpdatedStatus}
                />
                <ConfirmStatusAlert
                    actionId={this.state.currentActionId}
                    showConfirmationModal={this.state.showConfirmationModal}
                    setConfirmationModal={this.toggleConfirmationModalOpen}
                    onConfirm={this.checkChildrenAndProceed}
                />
                {/* Propagate modal for bulk updates */}
                {showPropagateModal && (
                    <Dialog
                        icon="info-sign"
                        onClose={this.handlePropagateCancel}
                        isOpen={showPropagateModal}
                        title="Update Action Status"
                    >
                        <div className={Classes.DIALOG_BODY}>
                            <p>
                                Would you like to update the status of all
                                related sub-tasks as well?
                            </p>
                        </div>
                        <div className={Classes.DIALOG_FOOTER}>
                            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                <Button
                                    intent="primary"
                                    large
                                    onClick={() =>
                                        this.handlePropagateConfirm(true)
                                    }
                                >
                                    Yes
                                </Button>
                                <Button
                                    intent="primary"
                                    large
                                    onClick={() =>
                                        this.handlePropagateConfirm(false)
                                    }
                                >
                                    No
                                </Button>
                                <Button
                                    intent="lynch-underline"
                                    large
                                    onClick={this.handlePropagateCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                )}
            </Card>
        )
    }
}

const mapStateToProps = (state, ownProps) => ({
    actionStatus: getAction(state, ownProps.runbookId, ownProps.id),
    selectedActions: getSelectedActions(state, state.ui.runbook.activeRunbook),
    awaitingActionUpdate:
        state.runbooks.byId?.[state.ui.runbook.activeRunbook]
            ?.awaitingActionUpdate,
    stateFromProps: state,
})

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    actions: bindActionCreators(
        {
            asyncUpdateRunbookSession,
            bulkUpdateActions,
            openContactSearch,
            toggleControl,
            toggleFreezeOwner,
            refreshFullRunbook: (runbookId, params) =>
                refreshSolution(runbookId, null, params),
        },
        dispatch
    ),
})

export default connect(mapStateToProps, mapDispatchToProps)(ManageControls)
